// Past runs: pick a date → list that day's runs → open one (re-renders the
// saved plan + its inputs) or select two to compare side by side.

import { type CSSProperties, useEffect, useState } from "react";

import { downloadRunInput, getRun, listRunDates, listRuns } from "../api/client";
import { formatClock } from "../lib/format";
import { useT } from "../lib/i18n";
import type { RunDate, RunDetail, RunSummary } from "../types";
import { ResultsView } from "./ResultsView";

export function HistoryView({ onOpenPlan }: { onOpenPlan?: (runId: string) => void } = {}) {
  const t = useT();
  const [dates, setDates] = useState<RunDate[]>([]);
  const [date, setDate] = useState("");
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [compare, setCompare] = useState<RunDetail[] | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const ds = await listRunDates();
        setDates(ds);
        if (ds.length) {
          setDate(ds[0].date);
          setRuns(await listRuns(ds[0].date));
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function pickDate(d: string) {
    setDate(d);
    setSelected(new Set());
    setLoading(true);
    try {
      setRuns(await listRuns(d));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  async function openCompare() {
    const ids = [...selected].slice(0, 2);
    try {
      const both = await Promise.all(ids.map(getRun));
      setCompare(both);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // ── Single run: re-render the saved plan + its inputs ───────────────────
  if (detail) {
    return (
      <div className="results">
        <div className="results-toolbar">
          <button className="btn btn-ghost" onClick={() => setDetail(null)}>
            {t("history.backToRuns")}
          </button>
          <button
            className="btn"
            onClick={() => void doDownload(detail.id, setError)}
          >
            {t("history.downloadInput")}
          </button>
        </div>
        <InputPanel run={detail} />
        {detail.result ? (
          <ResultsView
            result={detail.result}
            onNewPlan={() => setDetail(null)}
            onFinalize={onOpenPlan ? () => onOpenPlan(detail.id) : undefined}
          />
        ) : (
          <p className="upload-status error">
            {t("history.runFailed")} {detail.error ?? "unknown error"}
          </p>
        )}
      </div>
    );
  }

  // ── Compare two runs ────────────────────────────────────────────────────
  if (compare) {
    return <CompareView runs={compare} onBack={() => setCompare(null)} />;
  }

  // ── List ────────────────────────────────────────────────────────────────
  return (
    <section className="starter" style={{ maxWidth: 1100 }}>
      <div className="starter-card" style={{ maxWidth: "none" }}>
        <h1>{t("history.title")}</h1>
        <p className="starter-desc">{t("history.desc")}</p>

        <div className="config-row" style={{ alignItems: "flex-end" }}>
          <label className="control">
            <span className="control-label">{t("history.runDate")}</span>
            <select value={date} onChange={(e) => pickDate(e.target.value)}>
              {dates.length === 0 && <option value="">{t("history.noRuns")}</option>}
              {dates.map((d) => (
                <option key={d.date} value={d.date}>
                  {d.date} ({d.n})
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn"
            disabled={selected.size !== 2}
            onClick={openCompare}
          >
            {selected.size === 2
              ? t("history.compareReady")
              : t("history.comparePartial", { n: selected.size })}
          </button>
        </div>

        {error && <p className="upload-status error">{error}</p>}

        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}></th>
                <th style={th}>Time</th>
                <th style={th}>Source</th>
                <th style={th}>Status</th>
                <th style={thNum}>Pallets</th>
                <th style={thNum}>Trucks</th>
                <th style={thNum}>Miles</th>
                <th style={thNum}>Hours</th>
                <th style={thNum}>Cost $</th>
                <th style={thAction}>Input</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && !loading && (
                <tr>
                  <td style={td} colSpan={10}>
                    {t("history.noRunsDate")}
                  </td>
                </tr>
              )}
              {runs.map((r) => (
                <tr key={r.id}>
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                  </td>
                  <td style={td}>{fmtTime(r.created_at)}</td>
                  <td style={td}>{r.source}</td>
                  <td style={td}>{r.status}</td>
                  <td style={tdNum}>{r.n_pallets ?? "—"}</td>
                  <td style={tdNum}>{r.n_trucks ?? "—"}</td>
                  <td style={tdNum}>{fmtNum(r.total_miles, 0)}</td>
                  <td style={tdNum}>{fmtNum(r.total_hours, 1)}</td>
                  <td style={tdNum}>{fmtNum(r.cost_total_usd, 0)}</td>
                  <td style={tdAction}>
                    <button className="btn" onClick={() => void openRun(r.id, setDetail, setError)}>
                      {t("history.open")}
                    </button>
                    <button
                      className="btn btn-primary"
                      style={{ marginLeft: 6 }}
                      title={t("history.downloadTip")}
                      onClick={() => void doDownload(r.id, setError)}
                    >
                      {t("history.inputBtn")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

async function openRun(
  id: string,
  setDetail: (d: RunDetail) => void,
  setError: (e: string) => void,
) {
  try {
    setDetail(await getRun(id));
  } catch (e) {
    setError(e instanceof Error ? e.message : String(e));
  }
}

// Fetch a run's exact input and trigger a browser download (CSV for upload
// runs, JSON for picker runs — the server sets the filename).
async function doDownload(id: string, setError: (e: string) => void) {
  try {
    const { blob, filename } = await downloadRunInput(id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    setError(e instanceof Error ? e.message : String(e));
  }
}

function InputPanel({ run }: { run: RunDetail }) {
  const t = useT();
  const orders = (run.input?.orders as unknown[] | undefined)?.length;
  const fleet = run.fleet ?? {};
  return (
    <div style={inputPanel}>
      <strong>{t("history.inputs")}</strong> · {fmtTime(run.created_at)} ·{" "}
      {t("history.source")} {run.source}
      {orders != null && <> · {t("history.ordersSelected", { n: orders })}</>}
      {run.shipments && <> · {t("history.palletsIn", { n: run.shipments.length })}</>}
      <span style={{ marginLeft: 12 }}>
        {t("history.fleet", {
          f26: fleet.num_26ft ?? 0,
          f53: fleet.num_53ft ?? 0,
          f3pl: threeplTrucks(fleet.num_3pl_slots),
        })}
        {fleet.departures && <> · {t("history.departures", { list: departureList(run) })}</>}
      </span>
    </div>
  );
}

// "T1 8:30 AM, T2 6:00 AM": each truck's departure as the run was given it.
// The engine takes 3PL as trip slots, two per 3PL truck; show trucks.
function threeplTrucks(slots: number | undefined): number {
  return Math.ceil((slots ?? 0) / 2);
}

function departureList(run: RunDetail): string {
  return [
    ...(run.fleet?.departures ?? []).map((m, i) => `T${i + 1} ${formatClock(m)}`),
    ...(run.fleet?.threepl_departures ?? []).map((m, i) => `3PL${i + 1} ${formatClock(m)}`),
  ].join(", ");
}

function CompareView({ runs, onBack }: { runs: RunDetail[]; onBack: () => void }) {
  const t = useT();
  const [a, b] = runs;
  const rows: [string, (r: RunDetail) => string][] = [
    ["Time", (r) => fmtTime(r.created_at)],
    ["Source", (r) => r.source],
    ["Status", (r) => r.status],
    ["Orders in", (r) => String((r.input?.orders as unknown[] | undefined)?.length ?? "—")],
    ["Pallets", (r) => String(r.n_pallets ?? "—")],
    ["Trucks", (r) => String(r.n_trucks ?? "—")],
    ["Miles", (r) => fmtNum(r.total_miles, 0)],
    ["Hours", (r) => fmtNum(r.total_hours, 1)],
    ["Cost $", (r) => fmtNum(r.cost_total_usd, 0)],
    ["Fleet", (r) => `${r.fleet?.num_26ft ?? 0}×26 / ${r.fleet?.num_53ft ?? 0}×53 / ${threeplTrucks(r.fleet?.num_3pl_slots)} 3PL`],
    ["Departures", (r) => departureList(r) || "—"],
  ];
  return (
    <section className="starter" style={{ maxWidth: 760 }}>
      <div className="starter-card" style={{ maxWidth: "none" }}>
        <div className="results-toolbar">
          <h1>{t("history.compareRuns")}</h1>
          <button className="btn btn-ghost" onClick={onBack}>
            {t("history.backToRuns")}
          </button>
        </div>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}></th>
              <th style={th}>{t("history.runA")}</th>
              <th style={th}>{t("history.runB")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, get]) => {
              const va = get(a);
              const vb = get(b);
              const diff = va !== vb;
              return (
                <tr key={label} style={diff ? { background: "rgba(210,150,20,0.10)" } : undefined}>
                  <td style={{ ...td, fontWeight: 600 }}>{label}</td>
                  <td style={td}>{va}</td>
                  <td style={td}>{vb}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>
          {t("history.diffNote")}
        </p>
      </div>
    </section>
  );
}

function fmtTime(iso: string): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}
function fmtNum(n: number | null, d: number): string {
  return n == null ? "—" : n.toFixed(d);
}

const tableWrap: CSSProperties = {
  marginTop: 12,
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: 8,
  overflow: "auto",
};
const table: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid rgba(0,0,0,0.12)",
  whiteSpace: "nowrap",
};
const thNum: CSSProperties = { ...th, textAlign: "right" };
const td: CSSProperties = { padding: "6px 10px", borderBottom: "1px solid rgba(0,0,0,0.06)" };
const tdNum: CSSProperties = { ...td, textAlign: "right" };
// Pin the actions column to the right edge so "Open" / download stay visible
// even when the wide table scrolls horizontally on a narrow viewport.
const thAction: CSSProperties = {
  ...th,
  position: "sticky",
  right: 0,
  background: "var(--surface, #fff)",
  zIndex: 2,
  borderLeft: "1px solid rgba(0,0,0,0.10)",
};
const tdAction: CSSProperties = {
  ...td,
  position: "sticky",
  right: 0,
  background: "var(--surface, #fff)",
  zIndex: 1,
  borderLeft: "1px solid rgba(0,0,0,0.10)",
  whiteSpace: "nowrap",
};
const inputPanel: CSSProperties = {
  fontSize: 13,
  padding: "8px 12px",
  background: "rgba(0,0,0,0.03)",
  borderRadius: 8,
  margin: "8px 0",
};
